/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import DatePicker from "react-datepicker";

export interface IAddAdditionalInfoInput {
    orderReferenceRef?: React.RefObject<HTMLInputElement>;
    orderNotesRef?: React.RefObject<HTMLInputElement>;
    hasError: boolean;
    orderReference: string;
    orderNotes: string;
    requestedShippingDate: Date | null;
    resources: {
        orderReferenceInputLabel: string;
        orderNotesInputLabel: string;
        requestedShippingDateInputLabel: string;
        noRequestedShippingDatePlaceholder: string;
        additionalInfoErrortext: string;
        saveBtnLabel: string;
        cancelBtnLabel: string;
    };
    canSubmit: boolean;
    canCancel: boolean;
    onOrderReferenceChange(orderReference: string): void;
    onOrderNotesChange(orderNotes: string): void;
    onRequestedShippingDateChange(requestedShippingDate: Date | null): void;
    onSubmit(): void;
    onCancel(): void;
}

export interface IAddAdditionalInfo {
    addFormProps: INodeProps;
    submitButton: React.ReactNode;
    cancelButton: React.ReactNode;
    inputGroupProps: INodeProps;
    orderReferenceProps: {
        label: React.ReactNode;
        input: React.ReactNode;
    };
    orderNotesProps: {
        label: React.ReactNode;
        input: React.ReactNode;
    };
    requestedShippingDateProps: {
        label: React.ReactNode;
        input: React.ReactNode;
    }
    error: React.ReactNode;
}

/**
 * On Order Reference Input change handler function.
 * @param onOrderReferenceChange -On change input function.
 * @returns Call of onOrderReferenceChange function.
 */
 const onOrderReferenceInputChangeHandler = (onOrderReferenceChange: (orderReference: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    onOrderReferenceChange(event.target.value || '');
};

/**
 * On Order Notes Input change handler function.
 * @param onOrderNotesChange -On change input function.
 * @returns Call of onOrderNotesChange function.
 */
const onOrderNotesInputChangeHandler = (onOrderNotesChange: (orderNotes: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    onOrderNotesChange(event.target.value || '');
};

const getAddAdditionalInfo = ({
    hasError,
    orderReferenceRef, 
    orderNotesRef, 
    orderReference,
    orderNotes,
    requestedShippingDate,
    canSubmit,
    canCancel,
    onOrderReferenceChange, 
    onOrderNotesChange, 
    onRequestedShippingDateChange,
    onSubmit,
    onCancel,
    resources: { 
        orderReferenceInputLabel, 
        orderNotesInputLabel, 
        requestedShippingDateInputLabel, 
        noRequestedShippingDatePlaceholder,
        additionalInfoErrortext, 
        saveBtnLabel, 
        cancelBtnLabel }
}: IAddAdditionalInfoInput): IAddAdditionalInfo => {
    const onOrderReferenceInputChange = onOrderReferenceInputChangeHandler(onOrderReferenceChange);
    const onOrderNotesInputChange = onOrderNotesInputChangeHandler(onOrderNotesChange);

    return {
        addFormProps: { className: 'lli-checkout-additional_info__add-additional-info' },
        submitButton: canSubmit && (
            <Button
                className='lli-checkout-additional_info__btn-save'
                title={saveBtnLabel}
                color='primary'
                onClick={onSubmit}
            >
                {saveBtnLabel}
            </Button>
        ),
        cancelButton: canCancel && (
            <Button
                className='lli-checkout-additional_info__btn-cancel'
                title={cancelBtnLabel}
                color='secondary'
                onClick={onCancel}
            >
                {cancelBtnLabel}
            </Button>
        ),
        inputGroupProps: { className: classnames('lli-checkout-additional_info__input', { 'is-invalid': hasError }) },
        orderReferenceProps: {
            label: (
                <label id='lli-checkout-additional_info__order-reference-label' className='lli-checkout-additional_info__order-reference-input-label'>
                    {orderReferenceInputLabel}
                </label>
            ),
            input: (
                <input
                    ref={orderReferenceRef}
                    type='text'
                    className='lli-checkout-additional_info__order-reference-input-text form-control'
                    aria-label={orderReferenceInputLabel}
                    onChange={onOrderReferenceInputChange}
                    value={orderReference}
                    maxLength={60}
                    aria-labelledby='lli-checkout-additional_info__order-reference-label lli-checkout-additional_info__error'
                />
            )
        },
        orderNotesProps: {
            label: (
                <label id='lli-checkout-additional_info__order-notes-label' className='lli-checkout-additional_info__order-notes-input-label'>
                    {orderNotesInputLabel}
                </label>
            ),
            input: (
                <input
                    ref={orderNotesRef}
                    type='text'
                    className='lli-checkout-additional_info__order-notes-input-text form-control'
                    aria-label={orderNotesInputLabel}
                    onChange={onOrderNotesInputChange}
                    value={orderNotes}
                    maxLength={256}
                    aria-labelledby='lli-checkout-additional_info__order-notes-label lli-checkout-additional_info__error'
                />
            )
        },
        requestedShippingDateProps: {
            label: (
                <label id='lli-checkout-additional_info__requested-shipping-date-label' className='lli-checkout-additional_info__requested-shipping-date-input-label'>
                    {requestedShippingDateInputLabel}
                </label>
            ),
            input: (
                <span>
                    <DatePicker
                        selected={requestedShippingDate}
                        onChange={onRequestedShippingDateChange}
                        dateFormat="MMMM d, yyyy"
                        isClearable
                        placeholderText={noRequestedShippingDatePlaceholder}
                        popperModifiers={[
                            {
                              name: "offset",
                              options: {
                                offset: [5, 10],
                              },
                            }
                        ]}
                    />
                </span>
            )
        },
        error: hasError && (
            <span
                id='lli-checkout-additional_info__error'
                className='lli-checkout-additional_info__input-error'
                role='alert'
                aria-live='polite'
            >
                {additionalInfoErrortext}
            </span>
        )
    };
};

export default getAddAdditionalInfo;
